import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { QUERY_GET_PROPERTIES_BY_CURRENCY } from 'common/queries/property';
import { debounce, isEqual } from 'lodash';
import { bookingJourneyType } from 'common/constants/booking';
import { propertyDisabledReasons } from 'common/constants/landlord';
import { apCategory } from 'common/constants/property';
import { formatSelectedProperties, shouldDisableCurrency, getDisabledReason } from './helpers';

export const useAppliedProperties = ({
  landlordId,
  selectedProperties,
  selectedCurrency,
  onChange,
  paymentPlanId,
  isDepositRequired,
}) => {
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSizeRight, setPageSizeRight] = useState(10);
  const [pageNumberRight, setPageNumberRight] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedItems, setSelectedItems] = useState(formatSelectedProperties(selectedProperties));
  const [currency, setCurrency] = useState(selectedCurrency);
  const [disabledCurrency, setDisabledCurrency] = useState(
    shouldDisableCurrency(selectedProperties),
  );
  const [isTouched, setIsTouched] = useState(false);
  const [selectedItemSnapshot, setSelectedItemSnapshot] = useState([]);

  const prevSelectedItemsRef = useRef();
  const prevSelectedPropertiesRef = useRef(selectedProperties);

  const bookingJourneys = [bookingJourneyType.INSTANT_BOOK, bookingJourneyType.SEMI_AUTOMATIC];

  const columns = [
    {
      dataIndex: 'name',
      title: t('landlord:landlord.payment.long_tail.initial_setup.label.property_name'),
      render: (text, record) => (
        <span>
          {text}
          {record.disabled && (
            <Tooltip title={getDisabledReason(record.reason, t)}>
              <ExclamationCircleOutlined className="applied-properties__tooltip-icon" />
            </Tooltip>
          )}
        </span>
      ),
    },
  ];

  const [getProperties, { loading: isLoading }] = useLazyQuery(QUERY_GET_PROPERTIES_BY_CURRENCY, {
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      const propertyNodes = data.properties?.edges.map(edge => {
        const property = edge.node;
        if (property.status === 'PUBLISHED') {
          return { ...property, disabled: true, reason: propertyDisabledReasons.PUBLISHED };
        } else if (property.paymentPlanId && property.paymentPlanId !== paymentPlanId) {
          return { ...property, disabled: true, reason: propertyDisabledReasons.LINKED };
        } else if (property.apCategory !== apCategory.KEY && isDepositRequired) {
          return { ...property, disabled: true, reason: propertyDisabledReasons.NOT_KEY };
        }

        return property;
      });

      setProperties(propertyNodes || []);
      setTotalCount(data.properties?.totalCount || 0);
    },
    onError: error => {
      console.error('Error fetching properties:', error);
    },
  });

  const handleSelectCurrency = (value, isInitial = false) => {
    setCurrency(value);
    const variables = {
      currency: value,
      pageNumber: 1,
      pageSize: 10,
      bookingJourneys,
    };
    if (landlordId) {
      variables.landlordIds = [landlordId];
    }
    getProperties({ variables });
    setPageNumber(1);
    setPageSize(10);
    setPageNumberRight(1);
    setPageSizeRight(10);
    if (!isInitial) {
      setSelectedItems([]);
    }
  };

  const handleSearch = useCallback(
    debounce((direction, value) => {
      if (direction === 'left') {
        const variables = {
          currency,
          name: value,
          pageNumber: 1,
          pageSize,
          bookingJourneys,
        };
        if (landlordId) {
          variables.landlordIds = [landlordId];
        }
        if (value === undefined || value === null || value === '' || value.trim() === '') {
          delete variables.name;
        }
        getProperties({ variables });
      } else {
        if (!value || value.trim() === '') {
          setSelectedItems(selectedItemSnapshot);
          setPageNumberRight(1);
        } else {
          const filteredItems = selectedItems.filter(item =>
            item?.name.toLowerCase().includes(value.toLowerCase()),
          );
          setSelectedItems(filteredItems);
          setPageNumberRight(1);
        }
      }
    }, 300),
    [currency, landlordId, pageSize, bookingJourneys, selectedItems],
  );

  useEffect(() => {
    if (!isEqual(prevSelectedItemsRef.current, selectedItems)) {
      prevSelectedItemsRef.current = selectedItems;
      onChange?.(selectedItems, isTouched);
    }
  }, [selectedItems, onChange]);

  useEffect(() => {
    if (selectedCurrency) {
      handleSelectCurrency(selectedCurrency, true);
    }
  }, [selectedCurrency]);

  useEffect(() => {
    if (!isEqual(prevSelectedPropertiesRef.current, selectedProperties)) {
      prevSelectedPropertiesRef.current = selectedProperties;
      setDisabledCurrency(shouldDisableCurrency(selectedProperties));
      setSelectedItems(formatSelectedProperties(selectedProperties));
      setSelectedItemSnapshot(formatSelectedProperties(selectedProperties));
    }
  }, [selectedProperties]);

  return {
    columns,
    bookingJourneys,
    isLoading,
    properties,
    pageSize,
    selectedItems,
    pageNumber,
    totalCount,
    currency,
    pageSizeRight,
    pageNumberRight,
    disabledCurrency,
    isTouched,
    t,
    getProperties,
    getDisabledReason,
    handleSearch,
    setSelectedItems,
    setPageSize,
    setPageNumber,
    setCurrency,
    setPageSizeRight,
    setPageNumberRight,
    setIsTouched,
    setSelectedItemSnapshot,
    handleSelectCurrency,
  };
};
